
















import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Pagination } from 'element-ui';

const DEFAULT_SIZES = [25, 50, 100];

@Component({
  components: {
    'el-pagination': Pagination
  }
})
export default class PaginationComponent extends Vue {
  @Prop({ type: Number, required: true }) private totalCount!: number;
  @Prop({ type: Number, required: true }) private paginationSize!: number;
  @Prop({ type: Number, required: true }) private currentPage!: number;
  @Prop({ type: Array, default: () => { return DEFAULT_SIZES; } }) private pageSize!: number[];

  private requestedPage: number;
  private totalCountUpdated = false;

  get size(): number {
    let newSize = this.paginationSize;
    if (this.$route?.query?.size) { // priority to the route
      const s = parseInt((this.$route?.query?.size as string), 10);
      newSize = this.determineClosestSize(s, this.pageSize);
    }
    this.$emit('changeSize', newSize);
    return newSize;
  }

  set size(value) {
    this.replaceQueryArgs(this.page, value, false);
    this.$emit('changeSize', value);
  }

  get page(): number {
    let newPage = this.currentPage;
    if (this.$route?.query?.page) { // priority to the route
      const p = parseInt((this.$route?.query?.page as string), 10);
      // first save the requested page if not saved yet
      this.requestedPage = this.requestedPage ? this.requestedPage : p;

      // then process with the calculation
      newPage = this.determineClosestPage(p, this.size, this.totalCount);
    }
    this.$emit('changePage', newPage);
    return newPage;
  }

  set page(value) {
    if (!this.totalCountUpdated) {
      return;
    }
    this.replaceQueryArgs(value, this.size, false);
    this.$emit('changePage', value);
  }

  private replaceQueryArgs(page: number, size: number, replace: boolean) {
    const routePage = parseInt((this.$route?.query?.page as string), 10);
    const routeSize = parseInt((this.$route?.query?.size as string), 10);
    if (page === routePage && size === routeSize) {
      return;
    }
    const routerArgs = {
      path: this.$route.path,
      query: {
        // keep existing query args
        ...this.$route.query,
        page: page.toString(),
        size: size.toString()
      }
    };
    (replace) ? this.$router.replace(routerArgs) : this.$router.push(routerArgs);
  }

  // keep size in sync with totalCount
  @Watch('totalCount')
  calculatePage(val: number) {
    if (!val || val <= 0) {
      this.totalCountUpdated = false;
    } else {
      const p = this.determineClosestPage(this.requestedPage, this.size, val);
      this.replaceQueryArgs(p, this.size, true);
      this.totalCountUpdated = true;
    }
  }

  /**
   * @param size the size to be tested
   * @param availableSizes the list of the possible values a size can take
   * @returns closest available size
   */
  private determineClosestSize(size: number, availableSizes: number[]): number {
    let closest = DEFAULT_SIZES[0]; // fallback
    for (let i = 0; i < availableSizes.length; i++) {
      if (size === availableSizes[i]) {
        return availableSizes[i];
      } else if (Math.abs(size - availableSizes[i]) < Math.abs(size - closest)) {
        closest = availableSizes[i];
      }
    }
    return closest;
  }

  /**
   * @param page the page to be tested
   * @param size the current size of a page
   * @param totalCount the total count of entries (used to determine the last page)
   * @returns closest available page
   */
  private determineClosestPage(page: number, size: number, totalCount: number): number {
    let closest = 1; // fallback
    if (page) {
      const lastPage = Math.ceil(totalCount / size);

      closest = (page <= lastPage) ? page : lastPage;
      closest = (page <= 0) ? 1 : page;
    }
    return closest;
  }
}
